import { defineStore } from 'pinia'

export type SemanticNotificationType = 'info' | 'success' | 'warning' | 'error'
export type LegacyNotificationType =
  | 'is-primary'
  | 'is-info'
  | 'is-success'
  | 'is-warning'
  | 'is-danger'

export type NotificationType = SemanticNotificationType | LegacyNotificationType

export type NotificationPosition =
  | 'top'
  | 'bottom'
  | 'is-top'
  | 'is-bottom'
  | 'is-top-right'
  | 'is-top-left'
  | 'is-bottom-right'
  | 'is-bottom-left'

export interface NotificationOptions {
  message: string
  type: NotificationType
  hasIcon?: boolean
  indefinite?: boolean
  closeable?: boolean
  position?: NotificationPosition
  autoClose?: boolean
  duration?: number
  'data-test-id'?: string
}

export interface QueuedNotification {
  id: number
  message: string
  type: SemanticNotificationType
  hasIcon: boolean
  indefinite: boolean
  closeable: boolean
  position: NotificationPosition
  autoClose: boolean
  duration?: number
  'data-test-id'?: string
}

const typeMap: Record<NotificationType, SemanticNotificationType> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
  'is-primary': 'info',
  'is-info': 'info',
  'is-success': 'success',
  'is-warning': 'warning',
  'is-danger': 'error',
}

const normalizeNotification = (
  options: NotificationOptions,
  id: number
): QueuedNotification => {
  const type = typeMap[options.type]
  const autoClose = options.autoClose ?? type === 'success'

  return {
    id,
    message: options.message,
    type,
    hasIcon: options.hasIcon ?? true,
    indefinite: options.indefinite ?? true,
    closeable: options.closeable ?? true,
    position: options.position ?? 'is-top',
    autoClose,
    duration: autoClose ? (options.duration ?? 5000) : options.duration,
    'data-test-id': options['data-test-id'],
  }
}

interface NotificationsState {
  activeNotification: QueuedNotification | null
  queue: QueuedNotification[]
  nextId: number
}

export const useNotificationsStore = defineStore('notifications', {
  state: (): NotificationsState => ({
    activeNotification: null,
    queue: [],
    nextId: 1,
  }),

  actions: {
    showNotification(options: NotificationOptions) {
      const notification = normalizeNotification(options, this.nextId)
      this.nextId += 1

      if (this.activeNotification) {
        this.queue.push(notification)
        return
      }

      this.activeNotification = notification
    },

    dismissNotification() {
      this.activeNotification = this.queue.shift() ?? null
    },

    clearNotifications() {
      this.activeNotification = null
      this.queue = []
    },
  },
})
